import { Injectable } from "@angular/core";
import { IProductNode } from "../../shared/interfaces/product-node.interface";
import { Product, SelectedProduct } from "../../shared/models/product.model";
import { TreeComponent } from "@odymaui/angular-tree-component";
import { IProductNodeLevels } from "../../shared/interfaces/product-node-level.interface";
import { SELECTOR_COMMON_CONSTANTS } from "../../shared/constants/selector.constant";
import { ProductMenuOption } from "../../shared/models/product-menu-option.model";

@Injectable({
    providedIn: 'root',
})

export class ProductTreeService {
    private nodeExpandIndex = 0;

    public deselectProductMenuOption(selections: Array<IProductNode>, productId: number, menuOption: number): Array<IProductNode> {
        if (!selections) {
            return [];
        }

        if (menuOption === -1) {
            return selections;
        }

        return selections.map(node =>
            productId === node.parentId || productId === node.id
                ? {
                    ...node,
                    isSelected: false,
                    menuOptions: node.menuOptions.map(option =>
                        option.level === menuOption ? { ...option, isSelected: false } : option
                    ),
                    children: this.deselectProductMenuOption(node.children, node.id, menuOption - 1)
                }
                : { ...node, children: this.deselectProductMenuOption(node.children, productId, menuOption) }
        );
    }

    public selectProductMenuOption(productNodes: Array<IProductNode>, productId: number, menuOption: number): Array<IProductNode> {
        if (!productNodes) {
            return [];
        }

        if (menuOption === -1) {
            return productNodes;
        }

        return productNodes.map(productNode =>
            productId === productNode.parentId || productId === productNode.id
                ? {
                    ...productNode,
                    isSelected: true,
                    menuOptions: productNode.menuOptions.map(option =>
                        option.level === menuOption
                            ? {
                                ...option,
                                isSelected: true
                            }
                            : option
                    ),
                    children: this.selectProductMenuOption(productNode.children, productNode.id, menuOption - 1)
                }
                : { ...productNode, children: this.selectProductMenuOption(productNode.children, productId, menuOption) }
        );
    }


    public deselectAllProductTreeNodes(selections: Array<IProductNode>): Array<IProductNode> {
        if (!selections) {
            return [];
        }

        return selections.map(node => ({
            ...node,
            isSelected: false,
            children: this.deselectAllProductTreeNodes(node.children)
        }));
    }

    public mapProductsToTreeNodes(products: ReadonlyArray<Product>, selectedProducts: Array<SelectedProduct>): Array<IProductNode> {
        const productNodes = products.map(
            (product: Product) =>
            ({
                id: product.id,
                categoryGuid: product.categoryGuid,
                parentId: product.parent?.id,
                parentName: product.parent?.name,
                name: product.name,
                level: product.level,
                isSelected: selectedProducts.map(sp => sp.categoryGuid).includes(product.categoryGuid),
                menuOptions: [],
                isSelectable: true,
                hierarchy: '',
                children: []
            } as IProductNode)
        );

        return this.mapChildProductsToParents(
            productNodes.filter((productNode: IProductNode) => !productNode.parentId),
            productNodes,
            products
        );
    }

    public deselectProductTreeNode(selectedProductNodes: Array<IProductNode>, productId: number): Array<IProductNode> {
        if (!selectedProductNodes) {
            return [];
        }

        return selectedProductNodes.map((productNode: IProductNode) =>
            productId === productNode.id
                ? {
                    ...productNode,
                    isSelected: false
                }
                : { ...productNode, children: this.deselectProductTreeNode(productNode.children, productId) }
        );
    }

    public flattenProductSelections(productNodes: Array<IProductNode>): Array<SelectedProduct> {
        if (!productNodes) {
            return [];
        }

        return productNodes
            .reduce<Array<SelectedProduct>>(
                (result, item) => [
                    ...result,
                    {
                        id: item.id,
                        categoryGuid: item.categoryGuid,
                        name: item.name,
                        hierarchy: item.hierarchy,
                        isSelected: item.isSelected,
                        parentId: item.parentId
                    } as SelectedProduct,
                    ...this.flattenProductSelections(item.children)
                ],
                []
            )
            .filter(s => s.isSelected);
    }

    private static buildMenuOptions(productLevel: number, allChildren: Array<Product>, name: string): Array<ProductMenuOption> {
        const maxChildLevel = Math.max(...allChildren.map(child => child.level));
        return maxChildLevel - productLevel < 0
            ? []
            : new Array(maxChildLevel - productLevel)
                .fill(1)
                .map((_, i) =>
                    i === 0
                        ? {
                            level: i + 1,
                            isSelected: false,
                            title: `Select ${name} and ${i + 1} level below it`
                        }
                        : {
                            level: i + 1,
                            isSelected: false,
                            title: `Select ${name} and ${i + 1} levels below it`
                        }
                );
    }

    private mapChildProductsToParents(
        parents: Array<IProductNode>,
        allProducts: Array<IProductNode>,
        products: ReadonlyArray<Product>
    ): Array<IProductNode> {
        return parents.map(({ id, categoryGuid, name, parentId, parentName, isSelected, level, isSelectable }) => ({
            id,
            categoryGuid,
            name,
            parentId,
            parentName,
            isSelected,
            level,
            hierarchy: '',
            isSelectable,
            menuOptions: [],
            children: this.mapChildProductsToParents(
                allProducts.filter(i => i.parentId === id),
                allProducts,
                products
            )
        }));
    }

    private buildProductHierarchy(productId: number, products: ReadonlyArray<Product>): string {
        const productsAndParents = products.reduce<Record<string, string | undefined>>((acc, item) => {
            acc[item['name']] = item['parent']?.name;
            return acc;
        }, {});

        let hierarchy = '';
        const foundProduct = products.find(p => p.id === productId);
        let parentName = foundProduct?.parent?.name;

        while (parentName) {
            hierarchy = `${parentName}/ ${hierarchy}`;
            parentName = productsAndParents[parentName];
        }

        return hierarchy.trim();
    }

    public expandTreeNodes(nodeIds: Array<number>, tree: TreeComponent): void {
        if (nodeIds?.length > 0) {
            nodeIds.forEach(item => {
                const nodeToBeExpanded = tree.treeModel.getNodeById(item);
                if (nodeToBeExpanded && nodeToBeExpanded.children?.length > 0) {
                    nodeToBeExpanded.expand();
                }
            });
        }
    }

    private updateNodeData(nodeData: any, storedProducts: Array<Product>): void {
        if (nodeData.menuOptions.length === 0 || nodeData.hierarchy === '') {
            nodeData.menuOptions = ProductTreeService.buildMenuOptions(
                nodeData.level,
                this.getAllChildrenForProduct(nodeData.id, storedProducts),
                nodeData.name
            );
            nodeData.hierarchy = this.buildProductHierarchy(nodeData.id, storedProducts);
        }
    }

    public updateNodeAndChildren(nodeData: any, storedProducts: Array<Product>, isRecursive: boolean): void {
        this.updateNodeData(nodeData, storedProducts);
        if (nodeData.children) {
            if (isRecursive) {
                nodeData.children.forEach((child: any) => this.updateNodeAndChildren(child, storedProducts, isRecursive));
            }
            else {
                nodeData.children.forEach((child: any) => this.updateNodeData(child, storedProducts));
            }
        }
    }

    public refreshNodeOptions(currentNode: IProductNode): IProductNode {
        let nodeStatus: IProductNodeLevels = {
            nonSelectedLevels: [],
            maxLevel: currentNode.level
        };
        nodeStatus = this.filterOutSelectedLevels(currentNode, nodeStatus);
        return this.setMenuOptions(currentNode, nodeStatus);
    }

    private filterOutSelectedLevels(currentNode: IProductNode, nodeStatus: IProductNodeLevels): IProductNodeLevels {
        currentNode.children.forEach(child => {
            nodeStatus = this.filterOutSelectedLevels(child, nodeStatus);
        });
        if (currentNode.level > nodeStatus.maxLevel) {
            nodeStatus.maxLevel = currentNode.level;
        }
        if (
            !currentNode.isSelected &&
            !nodeStatus.nonSelectedLevels.includes(currentNode.level)
        ) {
            nodeStatus.nonSelectedLevels.push(currentNode.level);
        }
        return nodeStatus;
    }


    public getAllChildrenForProduct(productId: number, products: ReadonlyArray<Product>): Array<Product> {
        let children = products.filter(product => product.parent?.id === productId);
        let allChildren: Array<Product> = [];
        while (children.length !== 0) {
            allChildren = [...allChildren, ...children];
            children = children.flatMap(child =>
                products.filter(product => product.parent?.id === child.id)
            );
        }
        return allChildren;
    }

    public getExpandedNodeIds(tree: TreeComponent): Array<number> {
        const expandedNodeIdsProxy = tree.treeModel.expandedNodeIds;
        return Object.keys(expandedNodeIdsProxy).filter(
            key => expandedNodeIdsProxy[key]
        ).map(Number);
    }

    public iterateNodesToRefresh(nodeToRefresh: IProductNode, tree: TreeComponent, isSearched: boolean, isCalledFromComponent: boolean): IProductNode {
        if (isSearched) {
            this.nodeExpandIndex = isCalledFromComponent ? 0 : this.nodeExpandIndex;
            this.determineParentId(nodeToRefresh, tree);
        }
        if (nodeToRefresh.children.length === 0) {
            return nodeToRefresh;
        }
        nodeToRefresh = this.refreshNodeOptions(nodeToRefresh);
        nodeToRefresh.children.forEach((child: IProductNode) => this.iterateNodesToRefresh(child, tree, isSearched, false));
        return nodeToRefresh;
    }

    private determineParentId(nodeToRefresh: IProductNode, tree: TreeComponent): number | null {
        let parentId: number | null = null;
        if (nodeToRefresh.isSelected && tree && nodeToRefresh.parentId) {
            parentId = nodeToRefresh.parentId;
            while (parentId && this.nodeExpandIndex <= SELECTOR_COMMON_CONSTANTS.numberHundred) {
                const node = tree.treeModel.getNodeById(parentId);
                if (node) {
                    node.expand();
                    parentId = node.data.parentId;
                    this.nodeExpandIndex++;
                } else {
                    parentId = null;
                }
            }
        }
        return parentId;
    }

    private setMenuOptions(currentNode: IProductNode, nodeStatus: IProductNodeLevels): IProductNode {
        let nonSelectedLevels = nodeStatus.nonSelectedLevels;
        const maxLevel = nodeStatus.maxLevel - currentNode.level;
        currentNode.menuOptions.forEach(f => {
            this.deterMineTitleBasedOnLevelSelect(f, currentNode);
        });

        if (!nonSelectedLevels.includes(currentNode.level)) {
            nonSelectedLevels.push(currentNode.level);
        }
        {
            nonSelectedLevels = nonSelectedLevels.map(level => level - currentNode.level);
            const menuOptionLevelsToDeselect = new Array(maxLevel)
                .fill(1)
                .map((_, i) => i + 1)
                .filter(level => !nonSelectedLevels.includes(level));

            currentNode.menuOptions.forEach(option => {
                this.deterMineTitleBasedOnLevelDeSelect(option, currentNode, menuOptionLevelsToDeselect);
            });

        }
        return currentNode;
    }

    private deterMineTitleBasedOnLevelSelect(productMenu: ProductMenuOption, currentNode: IProductNode) {
        if (productMenu.level === 1) {
            productMenu.title = `Select ${currentNode.name} and ${productMenu.level} level below it`;
        } else {
            productMenu.title = `Select ${currentNode.name} and ${productMenu.level} levels below it`;
        }
        productMenu.isSelected = false;
    }

    private deterMineTitleBasedOnLevelDeSelect(
        productMenu: ProductMenuOption,
        currentNode: IProductNode,
        menuOptionLevelsToDeselect: Array<number>
    ) {
        if (menuOptionLevelsToDeselect.includes(productMenu.level)) {
            if (productMenu.level === 1) {
                productMenu.title = `Deselect ${currentNode.name} and ${productMenu.level} level below it`;
            } else {
                productMenu.title = `Deselect ${currentNode.name} and ${productMenu.level} levels below it`;
            }
            productMenu.isSelected = true;
        }
    }
}